import React, { useState, useEffect } from "react";
import MainLayouts from "../../Layout/MainLayouts";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ReactComponent as ArrowLeft } from "bootstrap-icons/icons/arrow-left.svg";
import Spinner from "../spinner/Spinner";
import {
  useSupplyVendorById,
  useCreateSupplyVendor,
  useUpdateSupplyVendor,
} from "../../hooks/useInventory";

const emptyForm = {
  name: "",
  phone_number: "",
  email: "",
  address: "",
  contact_person_name: "",
  contact_person_phone: "",
  products_supplied: "",
  narration: "",
  status: "active",
};

const Field = ({ label, name, required, type = "text", as, form, errors, onChange }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {as === "textarea" ? (
      <textarea
        name={name}
        value={form[name]}
        onChange={onChange}
        rows={3}
        className={`w-full border ${errors[name] ? "border-red-500" : "border-gray-300"} rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange1`}
      />
    ) : (
      <input
        type={type}
        name={name}
        value={form[name]}
        onChange={onChange}
        className={`w-full border ${errors[name] ? "border-red-500" : "border-gray-300"} rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange1`}
      />
    )}
    {errors[name] && <p className="text-red-500 text-xs mt-1">{errors[name]}</p>}
  </div>
);

const SupplyVendorForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const vendorId = searchParams.get("id");
  const isEdit = !!vendorId;

  const { data: existingData, isLoading: loadingExisting } = useSupplyVendorById(vendorId);
  const createMutation = useCreateSupplyVendor();
  const updateMutation = useUpdateSupplyVendor();

  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (existingData?.data) {
      const v = existingData.data;
      setForm({
        name: v.name || "",
        phone_number: v.phone_number || "",
        email: v.email || "",
        address: v.address || "",
        contact_person_name: v.contact_person_name || "",
        contact_person_phone: v.contact_person_phone || "",
        products_supplied: v.products_supplied || "",
        narration: v.narration || "",
        status: v.status || "active",
      });
    }
  }, [existingData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Name is required";
    if (!form.phone_number.trim()) errs.phone_number = "Phone number is required";
    return errs;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    const mutation = isEdit ? updateMutation : createMutation;
    const payload = isEdit ? { ...form, vendor_id: parseInt(vendorId) } : form;

    mutation.mutate(payload, {
      onSuccess: () => navigate("/head-office/inventory/supply-vendors"),
    });
  };

  const isPending = createMutation.isLoading || updateMutation.isLoading;

  if (isEdit && loadingExisting) return <MainLayouts><Spinner loading /></MainLayouts>;

  return (
    <MainLayouts>
      <div className="p-6">
        <div className="flex items-center gap-4 mb-6">
          <button onClick={() => navigate("/head-office/inventory/supply-vendors")} className="text-orange1 hover:text-orange-600">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-2xl font-bold">{isEdit ? "Edit Supply Vendor" : "New Supply Vendor"}</h1>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Vendor Name" name="name" required form={form} errors={errors} onChange={handleChange} />
              <Field label="Phone Number" name="phone_number" required form={form} errors={errors} onChange={handleChange} />
              <Field label="Email" name="email" type="email" form={form} errors={errors} onChange={handleChange} />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  name="status"
                  value={form.status}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange1"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <Field label="Contact Person Name" name="contact_person_name" form={form} errors={errors} onChange={handleChange} />
              <Field label="Contact Person Phone" name="contact_person_phone" form={form} errors={errors} onChange={handleChange} />
              <div className="md:col-span-2">
                <Field label="Address" name="address" as="textarea" form={form} errors={errors} onChange={handleChange} />
              </div>
              <div className="md:col-span-2">
                <Field label="Products Supplied" name="products_supplied" as="textarea" form={form} errors={errors} onChange={handleChange} />
              </div>
              <div className="md:col-span-2">
                <Field label="Notes / Narration" name="narration" as="textarea" form={form} errors={errors} onChange={handleChange} />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => navigate("/head-office/inventory/supply-vendors")}
                className="px-5 py-2 border border-gray-300 rounded hover:bg-gray-50 transition text-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isPending}
                className="px-5 py-2 bg-orange1 text-white rounded hover:bg-orange-600 transition text-sm disabled:opacity-50"
              >
                {isPending ? "Saving..." : isEdit ? "Update Vendor" : "Create Vendor"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </MainLayouts>
  );
};

export default SupplyVendorForm;
